<template>
  <div>
    <div v-if="modoEdicion !== 'none'" class="d-flex justify-content-between mt-3">
      <DxButton
        v-if="hiddenTypesList.length > 0"
        v-bind="defaultButtonProps($t('adjuntosOperacionCardList.hiddenTypesSelector.label'), true)"
        @click="showHiddenTypesModal"
      />
      <DxButton
        v-if="!withExternalUploadBtn"
        v-bind="secondaryButtonProps($t('adjuntosOperacionCardList.uploadDocuments.label'))"
        @click="uploadDocuments"
        icon="fa-solid fa-upload"
      />
    </div>
    <div class="cards-grid mt-4">
      <div v-for="grupo in sortedGruposAdjuntos" :key="grupo.key" :class="{ 'd-none': hiddenCards.has(grupo.key) }" @dragover.prevent>
        <DocumentCard ref="documentCards" v-bind="getDocumentCardProps(grupo)" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import DocumentCard from "@components/document-card/DocumentCard.vue";
import type { IDocumentInitialState, IDocumentCardProps } from "@components/document-card/models/IDocumentCardProps";
import { useIntl } from "@services/i18n/i18nservice";
import { TipoDocumentoOperacionEnergiaEnum } from "@api/api";
import { ref, computed, onMounted, watch } from "vue";
import type { CardFile } from "@components/document-card/models/CardFile";
import { useModalService } from "@services/modal-service";
import InputSelectBox, { type InputSelectBoxModalResult } from "@components/dialogs/InputSelectBox.vue";
import { messageBoxResult } from "@components/dialogs/message-box";
import { DxButton } from "devextreme-vue/button";
import { defaultButtonProps, secondaryButtonProps } from "@helpers/button-helper";

const emit = defineEmits<{
  "documents:upload": [];
}>();

export interface AdjuntoOperacion {
  Id: string;
  IdTipoDocumento?: string | undefined;
  Tipo: string | TipoDocumentoOperacionEnergiaEnum;
  Nombre: string;
  Ruta: string;
  Descripcion: string | null;
}

interface EstructuraDocumentalOperacion {
  IdTipoDocumento?: string | undefined;
  TipoDocumento: string | TipoDocumentoOperacionEnergiaEnum;
  Requerido: boolean;
  Destacado: boolean;
  CodigoAgrupacion?: string | null | undefined;
  Plantilla?: string | null | undefined;
  RequiereDescripcion?: boolean;
}

const props = defineProps<{
  adjuntos: AdjuntoOperacion[];
  configAdjuntos: EstructuraDocumentalOperacion[];
  modoEdicion: "all" | "only-new-files" | "none";
  withExternalUploadBtn?: boolean;
}>();

interface GrupoAdjuntos {
  key: string; // Clave única para identificar el grupo (CodigoAgrupacion o TipoDocumento)
  tipos: EstructuraDocumentalOperacion[]; // Lista de tipos en el grupo
  codigoAgrupacion: string | null; // Código de agrupación si existe
  title: string; // Título traducido del grupo para mostrar en la card
}

const configAdjuntos = ref<EstructuraDocumentalOperacion[]>(props.configAdjuntos);
const intl = useIntl();
const documentCards = ref<InstanceType<typeof DocumentCard>[]>([]);
const hiddenCards = ref<Set<string>>(new Set());
const modal = useModalService();

const gruposAdjuntos = computed(() => {
  const gruposMap = new Map<string, GrupoAdjuntos>();

  // Creamos los grupos a partir de los tipos de documentos
  for (const tipo of configAdjuntos.value) {
    const key = tipo.CodigoAgrupacion ?? tipo.TipoDocumento;

    if (!gruposMap.has(key)) {
      gruposMap.set(key, {
        key: key,
        tipos: [],
        codigoAgrupacion: tipo.CodigoAgrupacion ?? null,
        title: "", // Se calculará después de ordenar los tipos
      });
    }

    gruposMap.get(key)!.tipos.push(tipo);
  }

  // Ordenamos los tipos dentro de cada grupo y calculamos su título
  for (const grupo of gruposMap.values()) {
    grupo.tipos.sort((a, b) => a.TipoDocumento.localeCompare(b.TipoDocumento));

    // Calcular el título con fallback si no existe traducción
    const baseKey = grupo.codigoAgrupacion ?? grupo.tipos[0].TipoDocumento;
    const i18nKey = `tipoDocumentoOperacionEnergia.values.${baseKey}`;
    grupo.title = intl.i18n.te(i18nKey) ? intl.i18n.t(i18nKey) : String(grupo.tipos[0].TipoDocumento);
  }

  return Array.from(gruposMap.values());
});

const sortedGruposAdjuntos = computed(() => {
  return [...gruposAdjuntos.value].sort((a, b) => {
    return a.title.localeCompare(b.title);
  });
});

const hiddenTypesList = computed(() => {
  return Array.from(hiddenCards.value).map((key) => {
    const grupo = gruposAdjuntos.value.find((g) => g.key === key);
    if (grupo) {
      return {
        Id: key,
        Denominacion: grupo.title,
      };
    }
    return {
      Id: key,
      Denominacion: key,
    };
  });
});

const getAccionesAdjunto = (grupo: GrupoAdjuntos): ("open" | "download" | "delete" | "replace" | "add" | "copy")[] => {
  switch (props.modoEdicion) {
    case "all":
      return ["open", "download", "delete", "replace", "add", "copy"];
    case "only-new-files":
      return ["open", "download", "add"];
    case "none":
      return ["open", "download"];
  }
};

const getDocumentCardProps = (grupo: GrupoAdjuntos): IDocumentCardProps => {
  const tiposDocumentos = grupo.tipos.map((tipo) => ({
    code: tipo.IdTipoDocumento ?? tipo.TipoDocumento,
    name: intl.i18n.te(`tipoDocumentoOperacionEnergia.values.${tipo.TipoDocumento}`)
      ? intl.i18n.t(`tipoDocumentoOperacionEnergia.values.${tipo.TipoDocumento}`)
      : String(tipo.TipoDocumento),
  }));

  // El grupo requiere descripción adicional si alguno de los tipos del grupo requiere descripción adicional
  const requiereDescripcion = grupo.tipos.some((tipo) => tipo.RequiereDescripcion || tipo.TipoDocumento === TipoDocumentoOperacionEnergiaEnum.Otros);
  const esRequerido = grupo.tipos.some((t) => t.Requerido);
  const templateUri = grupo.tipos.find((t) => t.Plantilla)?.Plantilla;

  // Obtener todos los documentos del grupo (de todos los tipos agrupados)
  const documentosIniciales = getInitialDocumentsForGrupo(grupo);

  return {
    title: grupo.title,
    allowedFileFormats: ["all"],
    initialDocumentList: documentosIniciales,
    actions: getAccionesAdjunto(grupo),
    maxFiles: 10,
    additionalDescriptionMode: requiereDescripcion ? "required" : "none",
    types: tiposDocumentos,
    required: props.modoEdicion === "none" ? false : esRequerido,
    templateUri: templateUri ?? undefined,
  } as IDocumentCardProps;
};

const getInitialDocumentsForGrupo = (grupo: GrupoAdjuntos): IDocumentInitialState[] => {
  const tiposEnGrupo = grupo.tipos.map((t) => String(t.IdTipoDocumento ?? t.TipoDocumento));
  return props.adjuntos
    .filter((a) => tiposEnGrupo.includes(String(a.IdTipoDocumento ?? a.Tipo)))
    .map(
      (a): IDocumentInitialState => ({
        id: a.Id,
        uri: a.Ruta,
        fileName: a.Nombre,
        description: a.Descripcion ?? undefined,
        typeCode: String(a.IdTipoDocumento ?? a.Tipo),
      }),
    );
};

const getFiles = (): CardFile[] => {
  const result: CardFile[] = [];

  for (let i = 0; i < sortedGruposAdjuntos.value.length; i++) {
    const documentCard = documentCards.value[i];

    if (documentCard) {
      const cardFiles = documentCard.getFiles();

      for (const fileId in cardFiles) {
        const file = cardFiles[fileId];
        result.push({
          id: file.id,
          file: file.file,
          fileName: file.fileName,
          remoteUri: file.remoteUri,
          uri: file.uri,
          status: file.status,
          description: file.description,
          typeCode: file.typeCode,
        });
      }
    }
  }

  return result;
};

const isValid = (): boolean => {
  let allValid = true;

  documentCards.value.forEach((card, i) => {
    const grupo = sortedGruposAdjuntos.value[i];

    const valid = hiddenCards.value.has(grupo.key) || !!card?.isValid;

    if (!valid) allValid = false;
  });

  return allValid;
};

const removeCard = (key: string): void => {
  const index = sortedGruposAdjuntos.value.findIndex((grupo) => grupo.key === key);
  if (index !== -1 && documentCards.value[index]) {
    documentCards.value[index].removeAllFiles();
  }

  hiddenCards.value.add(key);
};

const showCard = (key: string): void => {
  hiddenCards.value.delete(key);
};

const hideNonHighlightedCards = (): void => {
  for (const grupo of gruposAdjuntos.value) {
    const tiposEnGrupo = grupo.tipos.map((t) => String(t.TipoDocumento));
    const hasDocuments = props.adjuntos.some((a) => tiposEnGrupo.includes(String(a.Tipo)));
    const esDestacado = grupo.tipos.some((t) => t.Destacado);

    if (props.modoEdicion === "none") {
      if (!hasDocuments) {
        hiddenCards.value.add(grupo.key);
      }
    } else {
      if (!hasDocuments && !esDestacado) {
        hiddenCards.value.add(grupo.key);
      }
    }
  }
};

const showHiddenTypesModal = async (): Promise<void> => {
  if (hiddenTypesList.value.length === 0) {
    return;
  }

  const result = (await modal.showModal(
    InputSelectBox,
    {
      label: intl.i18n.t("adjuntosOperacionCardList.hiddenTypesSelector.label"),
      required: true,
      dataSource: hiddenTypesList.value,
    },
    {
      title: intl.i18n.t("adjuntosOperacionCardList.hiddenTypesSelector.title"),
      width: 500,
      height: 260,
    },
  )) as InputSelectBoxModalResult;

  if (result && result.result === messageBoxResult.ok && result.value) {
    const selectedKey = result.value as string;
    showCard(selectedKey);
  }
};

const uploadDocuments = (): void => {
  emit("documents:upload");
};

const resetAll = (): void => {
  hiddenCards.value = new Set();
  for (let i = 0; i < sortedGruposAdjuntos.value.length; i++) {
    const grupo = sortedGruposAdjuntos.value[i];
    documentCards.value[i]?.resetToInitial(getInitialDocumentsForGrupo(grupo));
  }

  hideNonHighlightedCards();
};

watch(
  () => props.adjuntos,
  () => {
    resetAll();
  },
);

watch(
  () => props.configAdjuntos,
  (newConfig) => {
    configAdjuntos.value = newConfig;
    resetAll();
  },
);

onMounted(() => {
  hideNonHighlightedCards();
});

defineExpose({
  getFiles,
  isValid,
  removeCard,
  showCard,
  resetAll,
});
</script>
<style scoped lang="scss">


.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  width: 100%;
}
</style>
